import { useCallback, useEffect, useState } from 'react';
import { api, fetchProtectedImage } from '../api/client';
import type { DriverDocumentView, DriverView } from '../api/types';
import { VEHICLE_LABELS } from '../api/types';
import { Alert, Spinner, formatDate } from '../components/common';

/**
 * The shop's side of driver onboarding: read the documents, approve or turn
 * down, suspend someone whose licence has lapsed.
 */
export function StaffDriversPage() {
  const [drivers, setDrivers] = useState<DriverView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDrivers(await api.driversList());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);

    try {
      await action();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!drivers) return <Spinner label="Loading drivers…" />;

  const waiting = drivers.filter((d) => d.status === 'PendingReview');
  const others = drivers.filter((d) => d.status !== 'PendingReview');

  return (
    <section className="page">
      <h1>Drivers</h1>

      {error && <Alert>{error}</Alert>}

      {waiting.length > 0 && (
        <>
          <h2 className="section-heading">Waiting for review ({waiting.length})</h2>
          {waiting.map((d) => (
            <DriverCard key={d.id} driver={d} busy={busy} onAct={act} />
          ))}
        </>
      )}

      <h2 className="section-heading">Everyone else</h2>
      {others.length === 0 && <p className="muted">No other drivers yet.</p>}
      {others.map((d) => (
        <DriverCard key={d.id} driver={d} busy={busy} onAct={act} />
      ))}
    </section>
  );
}

function DriverCard({
  driver,
  busy,
  onAct,
}: {
  driver: DriverView;
  busy: boolean;
  onAct: (action: () => Promise<unknown>) => Promise<void>;
}) {
  const [open, setOpen] = useState(driver.status === 'PendingReview');

  function withReason(prompt_: string, run: (reason: string) => Promise<unknown>) {
    const reason = window.prompt(prompt_);
    if (reason?.trim()) void onAct(() => run(reason.trim()));
  }

  return (
    <div className="panel driver-card">
      <div className="driver-card__head">
        <div>
          <strong>{driver.fullName}</strong>
          <p className="muted small">
            {VEHICLE_LABELS[driver.vehicle]}
            {driver.vehicleRegistration ? ` · ${driver.vehicleRegistration}` : ''} · {driver.phone}
          </p>
        </div>
        <StatusPill driver={driver} />
      </div>

      <p className="muted small">
        {driver.completedDeliveries} delivered
        {driver.submittedAt ? ` · applied ${formatDate(driver.submittedAt)}` : ''}
        {driver.isOnShift ? ' · on shift' : ''}
      </p>

      {driver.statusReason && <p className="field__error">{driver.statusReason}</p>}

      <button type="button" className="btn btn--ghost btn--block" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide documents' : `Show documents (${driver.documents.length})`}
      </button>

      {open && (
        <div className="driver-card__docs">
          {driver.documents.map((doc) => (
            <DocumentViewer key={doc.id} document={doc} />
          ))}
          {driver.missing.length > 0 && (
            <Alert tone="info">Still missing {driver.missing.length} document(s).</Alert>
          )}
        </div>
      )}

      <div className="driver-card__actions">
        {(driver.status === 'PendingReview' || driver.status === 'Rejected') && (
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || driver.missing.length > 0}
            onClick={() => void onAct(() => api.driverApprove(driver.id))}
          >
            Approve
          </button>
        )}

        {driver.status === 'PendingReview' && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            onClick={() => withReason('Why is this application being turned down?', (r) => api.driverReject(driver.id, r))}
          >
            Reject
          </button>
        )}

        {driver.status === 'Approved' && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            onClick={() => withReason('Why is this driver being suspended?', (r) => api.driverSuspend(driver.id, r))}
          >
            Suspend
          </button>
        )}

        {driver.status === 'Suspended' && (
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy}
            onClick={() => void onAct(() => api.driverReinstate(driver.id))}
          >
            Reinstate
          </button>
        )}
      </div>
    </div>
  );
}

function StatusPill({ driver }: { driver: DriverView }) {
  const tone =
    driver.status === 'Approved' ? 'pill pill--good'
    : driver.status === 'Rejected' || driver.status === 'Suspended' ? 'pill pill--bad'
    : 'pill';

  return <span className={tone}>{driver.status === 'PendingReview' ? 'Needs review' : driver.status}</span>;
}

/**
 * Documents need an Authorization header, so they cannot go straight into an
 * img tag — the bytes are fetched and turned into a blob URL, and revoked when
 * the row goes away so a long review session does not leak ID photos into memory.
 */
function DocumentViewer({ document }: { document: DriverDocumentView }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isPdf = document.contentType === 'application/pdf';

  useEffect(() => {
    let revoked: string | null = null;

    if (!isPdf) {
      fetchProtectedImage(`/api/drivers/documents/${document.id}`)
        .then((objectUrl) => {
          revoked = objectUrl;
          setUrl(objectUrl);
        })
        .catch((e: Error) => setError(e.message));
    }

    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [document.id, isPdf]);

  return (
    <div className="doc-view">
      <div className="doc-view__head">
        <strong>{document.label}</strong>
        {document.expiresOn && (
          <span className={document.isExpired ? 'pill pill--bad' : 'pill pill--muted'}>
            {document.isExpired ? 'Expired' : `Expires ${document.expiresOn}`}
          </span>
        )}
      </div>

      {error && <p className="field__error">{error}</p>}

      {isPdf ? (
        <a href={`/api/drivers/documents/${document.id}`} target="_blank" rel="noopener" className="btn btn--ghost">
          Open PDF
        </a>
      ) : url ? (
        <a href={url} target="_blank" rel="noopener">
          <img src={url} alt={document.label} className="doc-view__image" />
        </a>
      ) : (
        <span className="muted small">Loading…</span>
      )}
    </div>
  );
}
